import { createContext, useContext, useMemo, useState } from 'react';
import useGlobalFilterState from 'components/molecules/common/NewGlobalFilter/hooks/useGlobalFilterState';
import type { GlobalFilterState } from 'components/molecules/common/NewGlobalFilter/hooks/useGlobalFilterState';
import { useTpoGenConfig } from '../../context/CustomerConfigContext';
import type { PlanDetails } from '../CalendarPage/hooks/queries/useCalendarData';
import type { OptimizationStatus } from '../../utils/optimizationStatus';

interface PlanDetailsContextValue {
    activeView: 'calendar' | 'metric-view' | 'event';
    setActiveView: (view: 'calendar' | 'metric-view' | 'event') => void;
    currentPlanDetails: PlanDetails | null;
    setCurrentPlanDetails: (planDetails: PlanDetails | null) => void;
    editEventId: string | null;
    setEditEventId: (eventId: string | null) => void;
    editPromoId: string | null;
    setEditPromoId: (promoId: string | null) => void;
    autoAddPromo: boolean;
    setAutoAddPromo: (value: boolean) => void;
    hasUnsavedNoPromoEvent: boolean;
    setHasUnsavedNoPromoEvent: (value: boolean) => void;
    // Optimization toggle — shared across Calendar and Metric views so switching
    // to the optimized plan on the Calendar also drives the plan id used by the
    // Metric page data queries.
    optimizedPlanId: string | null;
    setOptimizedPlanId: (planId: string | null) => void;
    showOptimizedView: boolean;
    setShowOptimizedView: React.Dispatch<React.SetStateAction<boolean>>;
    optimizationPollingStatus: OptimizationStatus;
    setOptimizationPollingStatus: (status: OptimizationStatus) => void;
    // Independent filter states — the Calendar and Metric pages must not share a
    // filter selection. Each is seeded from its own config registry.
    calendarFilterState: GlobalFilterState;
    metricFilterState: GlobalFilterState;
    calendarSelectedColumnKeys: string[];
    setCalendarSelectedColumnKeys: React.Dispatch<React.SetStateAction<string[]>>;
    // Survives CalendarView unmount when navigating to event/promo (same as column keys).
    calendarGroupBy: string;
    setCalendarGroupBy: React.Dispatch<React.SetStateAction<string>>;
    calendarExpandedRowKeys: string[];
    setCalendarExpandedRowKeys: React.Dispatch<React.SetStateAction<string[]>>;
}

const noopFilterState: GlobalFilterState = {
    availableFilters: [],
    allCheckedAttribute: {},
    allSelectedValueForAllAttribute: {},
    applyFilterObject: {},
    selectedFilter: null,
    setSelectedFilter: () => {},
    tempApplyFilterObject: {},
    setTempApplyFilterObject: () => {},
    filters: {},
    filterApiVariables: undefined,
    resetFilterObject: () => {},
    onFilterSaveClickHandler: () => {},
};

const PlanDetailsContext = createContext<PlanDetailsContextValue>({
    activeView: 'calendar',
    setActiveView: () => {},
    currentPlanDetails: null,
    setCurrentPlanDetails: () => {},
    editEventId: null,
    setEditEventId: () => {},
    editPromoId: null,
    setEditPromoId: () => {},
    autoAddPromo: false,
    setAutoAddPromo: () => {},
    hasUnsavedNoPromoEvent: false,
    setHasUnsavedNoPromoEvent: () => {},
    optimizedPlanId: null,
    setOptimizedPlanId: () => {},
    showOptimizedView: false,
    setShowOptimizedView: () => {},
    optimizationPollingStatus: null,
    setOptimizationPollingStatus: () => {},
    calendarFilterState: noopFilterState,
    metricFilterState: noopFilterState,
    calendarSelectedColumnKeys: [],
    setCalendarSelectedColumnKeys: () => {},
    calendarGroupBy: 'none',
    setCalendarGroupBy: () => {},
    calendarExpandedRowKeys: [],
    setCalendarExpandedRowKeys: () => {},
});

export const usePlanDetailsContext = () => {
    return useContext(PlanDetailsContext);
};

export const PlanDetailsProvider = ({ children }: { children: React.ReactNode }) => {
    const tpoGenConfig = useTpoGenConfig();

    const [activeView, setActiveView] = useState<'calendar' | 'metric-view' | 'event'>('calendar');
    const [currentPlanDetails, setCurrentPlanDetails] = useState<PlanDetails | null>(null);
    const [editEventId, setEditEventId] = useState<string | null>(null);
    const [editPromoId, setEditPromoId] = useState<string | null>(null);
    const [autoAddPromo, setAutoAddPromo] = useState(false);
    const [hasUnsavedNoPromoEvent, setHasUnsavedNoPromoEvent] = useState(false);
    const [optimizedPlanId, setOptimizedPlanId] = useState<string | null>(null);
    const [showOptimizedView, setShowOptimizedView] = useState(false);
    const [optimizationPollingStatus, setOptimizationPollingStatus] = useState<OptimizationStatus>(null);
    const [calendarSelectedColumnKeys, setCalendarSelectedColumnKeys] = useState<string[]>(() =>
        tpoGenConfig.allDefaultColumns.filter((col) => col.defaultSelected).map((col) => col.key),
    );
    const [calendarGroupBy, setCalendarGroupBy] = useState('none');
    const [calendarExpandedRowKeys, setCalendarExpandedRowKeys] = useState<string[]>([]);

    const calendarFilterState = useGlobalFilterState(tpoGenConfig.calendarFilterKeys);
    const metricFilterState = useGlobalFilterState(tpoGenConfig.metricFilterKeys);

    const value = useMemo<PlanDetailsContextValue>(
        () => ({
            activeView,
            setActiveView,
            currentPlanDetails,
            setCurrentPlanDetails,
            editEventId,
            setEditEventId,
            editPromoId,
            setEditPromoId,
            autoAddPromo,
            setAutoAddPromo,
            hasUnsavedNoPromoEvent,
            setHasUnsavedNoPromoEvent,
            optimizedPlanId,
            setOptimizedPlanId,
            showOptimizedView,
            setShowOptimizedView,
            optimizationPollingStatus,
            setOptimizationPollingStatus,
            calendarFilterState,
            metricFilterState,
            calendarSelectedColumnKeys,
            setCalendarSelectedColumnKeys,
            calendarGroupBy,
            setCalendarGroupBy,
            calendarExpandedRowKeys,
            setCalendarExpandedRowKeys,
        }),
        [
            activeView,
            currentPlanDetails,
            editEventId,
            editPromoId,
            autoAddPromo,
            hasUnsavedNoPromoEvent,
            optimizedPlanId,
            showOptimizedView,
            optimizationPollingStatus,
            calendarFilterState,
            metricFilterState,
            calendarSelectedColumnKeys,
            calendarGroupBy,
            calendarExpandedRowKeys,
        ],
    );

    return <PlanDetailsContext.Provider value={value}>{children}</PlanDetailsContext.Provider>;
};
